import React, { useEffect, useMemo, useState } from 'react';
import axios from 'axios';
import { Search, Crown, Scissors, AlertTriangle, Egg } from 'lucide-react';
import { Input } from '../ui/input';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const shortDate = (d) => {
  if (!d) return '';
  const dt = new Date(String(d).length <= 10 ? `${d}T12:00:00` : d);
  return isNaN(dt) ? '' : dt.toLocaleDateString(undefined, { day: 'numeric', month: 'short' });
};

// One-line summary used in plain <select> lists
export const candidateLabel = (h) => {
  const bits = [];
  if (h.queenless) bits.push(`Queenless${h.queenless_date ? ` (${shortDate(h.queenless_date)})` : ''}`);
  if (h.split) bits.push(`${h.split.method}${h.split.role === 'parent' ? ' parent' : ''} ${shortDate(h.split.date)}`);
  if (h.cell_introduced) bits.push('cell already in');
  return `Hive ${h.hive_number}${h.yard ? ` · ${h.yard}` : ''}${bits.length ? ` — ${bits.join(' · ')}` : ''}`;
};

export const useCandidateHives = (open) => {
  const [hives, setHives] = useState(null);
  useEffect(() => {
    if (!open) return;
    const token = localStorage.getItem('token');
    axios.get(`${API}/queen-rearing/cell-candidate-hives`, { headers: { Authorization: `Bearer ${token}` } })
      .then(res => setHives(res.data.hives || [])).catch(() => setHives([]));
  }, [open]);
  return hives;
};

const Badges = ({ h }) => (
  <div className="flex flex-wrap gap-1 mt-1">
    {h.queenless && <span className="inline-flex items-center gap-1 text-[11px] font-semibold rounded-full bg-rose-100 text-rose-700 px-2 py-0.5"><AlertTriangle className="h-3 w-3" />Queenless{h.queenless_date ? ` · ${shortDate(h.queenless_date)}` : ''}</span>}
    {h.split && <span className={`inline-flex items-center gap-1 text-[11px] font-semibold rounded-full px-2 py-0.5 ${h.split.viable ? 'bg-amber-100 text-amber-800' : 'bg-stone-100 text-stone-600'}`}><Scissors className="h-3 w-3" />{h.split.method}{h.split.role === 'parent' ? ' (parent)' : h.split.role === 'same hive' ? ' (same hive)' : ''} · {shortDate(h.split.date)}</span>}
    {h.own_queen_cells && <span className="inline-flex items-center gap-1 text-[11px] rounded-full bg-violet-50 text-violet-700 px-2 py-0.5"><Egg className="h-3 w-3" />Own queen cells seen</span>}
    {h.cell_introduced && <span className="text-[11px] rounded-full bg-indigo-50 text-indigo-700 px-2 py-0.5">Cell already introduced</span>}
  </div>
);

const HiveRow = ({ h, onPick, busy }) => (
  <button disabled={busy} onClick={() => onPick(h)} data-testid={`cell-hive-option-${h.id}`}
    className="w-full text-left rounded-lg border border-stone-200 px-3 py-2 hover:border-indigo-400 hover:bg-indigo-50/50 transition-colors">
    <div className="flex items-center justify-between gap-2">
      <span className="font-medium text-stone-800">Hive {h.hive_number}{h.nickname ? ` "${h.nickname}"` : ''}</span>
      <span className="text-xs text-stone-500 truncate">{h.yard}</span>
    </div>
    <Badges h={h} />
  </button>
);

export const CellHivePicker = ({ open, onPick, busy }) => {
  const hives = useCandidateHives(open);
  const [q, setQ] = useState('');
  const filtered = useMemo(() => (hives || []).filter(h => !q || `${h.hive_number} ${h.nickname} ${h.yard}`.toLowerCase().includes(q.toLowerCase())), [hives, q]);

  if (hives === null) return <p className="text-sm text-stone-500 py-4 text-center">Loading hives…</p>;
  const best = filtered.filter(h => h.viable);
  const rest = filtered.filter(h => !h.viable);

  return (
    <div className="space-y-3" data-testid="cell-hive-picker">
      <div className="relative">
        <Search className="h-4 w-4 text-stone-400 absolute left-3 top-1/2 -translate-y-1/2" />
        <Input value={q} onChange={e => setQ(e.target.value)} placeholder="Search hive number or apiary" className="pl-9" data-testid="cell-hive-search" />
      </div>
      <div className="max-h-72 overflow-y-auto space-y-3 pr-1">
        {best.length > 0 && (
          <div>
            <p className="text-xs font-semibold uppercase tracking-wide text-amber-700 mb-1.5 flex items-center gap-1"><Crown className="h-3.5 w-3.5" />Best for a queen cell</p>
            <div className="space-y-1.5">{best.map(h => <HiveRow key={h.id} h={h} onPick={onPick} busy={busy} />)}</div>
          </div>
        )}
        {rest.length > 0 && (
          <div>
            <p className="text-xs font-semibold uppercase tracking-wide text-stone-500 mb-1.5">Other hives</p>
            <div className="space-y-1.5">{rest.map(h => <HiveRow key={h.id} h={h} onPick={onPick} busy={busy} />)}</div>
          </div>
        )}
        {filtered.length === 0 && <p className="text-sm text-stone-500 text-center py-3">No matching hives</p>}
      </div>
      <p className="text-xs text-stone-500">Queenless is taken from each hive's latest inspection. Reminders to check the queen are added for 10 and 21 days later.</p>
    </div>
  );
};

export default CellHivePicker;
